import { c as _c } from "react/compiler-runtime";
import { IcoForbid40, IcoSparkles16, IcoWarning40 } from '@onefootprint/icons';
import type { ChatSessionResponse } from '@onefootprint/request-types/dashboard';
import { Button, LoadingSpinner } from '@onefootprint/ui';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { type ReactNode, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { isPermissionError } from '@/utils/request-errors';
import type { AgentSessionInfo } from '../../hooks/use-agent-session';
import useBrowserPane from '../../hooks/use-browser-pane';
import useChatWs, { type ChatApprovalRequiredEvent, type ChatMessage, type ChatResourceEvent, type ChatTurnCompleteEvent, type SendMessageArgs } from '../../hooks/use-chat-ws';
import { useResourceContextSelection } from '../../hooks/use-resource-context-selection';
import type { ResumeChatSession, StartNewChat } from '../../types';
import { peekInitialPrompt } from '../../utils/initial-prompt';
import { BrowserAskCard, BrowserLiveView, BrowserPane, BrowserPaneSlot, BrowserPreviewCard } from '../browser-pane';
import { CARD_SECONDS, EASE_OUT } from '../browser-pane/browser-motion';
import ChatInput, { type ChatInputHandle, ChatInputMentionContext, type MentionAttrs } from '../chat-input';
import ChatPanel from '../chat-panel';
import { deriveBackgroundAgents, useSetBackgroundAgents } from '../chat-widget/background-agents-context';
import { deriveChatResources, useSetChatResources } from '../chat-widget/chat-resources-context';
import { composerActions } from '../composer-actions';
import ComposerDisclaimer from '../composer-disclaimer';
import FollowUpSuggestions from '../follow-up-suggestions';
import PercyDevtools from '../percy-devtools';
type ChatSessionProps = {
  /** Chat session id; the WS claims this session's stashed initial prompt on connect. */
  chatSessionId?: string;
  agentSession: AgentSessionInfo;
  /** Starts a new chat (the parent owns creation); used by the empty input + the no-resume send path. */
  onStartNewSession?: StartNewChat;
  /** Mints a live modal session for an existing default chat; absent for an external autopilot session. */
  onResumeSession?: ResumeChatSession;
  /** Copy shown in the empty CTA when no session has been started yet. */
  emptyStateLabel: string;
  /** Forwarded to `ChatPanel` to scroll the message list to the bottom on mount. */
  forceScrollToBottom?: boolean;
  /**
   * Called when the agent creates or updates a resource the consumer may be
   * displaying (artifact, gen-ui block, agent/investigation/entity). Switch on
   * `event.resource.kind` to decide what to refresh.
   */
  onResourceEvent?: (event: ChatResourceEvent) => void;
  /** Called on every websocket `ready` handshake — initial connection and each reconnect. */
  onReady?: () => void;
  onTurnComplete?: (event: ChatTurnCompleteEvent) => void;
  onApprovalRequired?: (event: ChatApprovalRequiredEvent) => void;
  /** Pre-loaded messages used to seed the chat panel before the websocket connects. */
  initialMessages?: ChatMessage[];
  /**
   * Sandbox state for the session. When set, the composer shows a locked
   * environment selector; omit while the session's env is still loading.
   */
  isLive?: boolean;
  /** A finished autopilot transcript: read-only, with an "ended" marker and a new-conversation placeholder. */
  isAutopilotEndedConversation?: boolean;
  /**
   * Starter prompts shown as chips on the zero-state (before the first message).
   * Pass entity/session-specific prompts to make them contextual; omit for the
   * generic Percy defaults.
   */
  starterPrompts?: string[];
  /** Composer placeholder once the conversation is under way; surfaces with their own subject keep their wording. */
  replyPlaceholder?: string;
};

// Generic zero-state starters for Percy (compliance/risk investigation). Parents
// with an entity in context should override via the `starterPrompts` prop.
const DEFAULT_STARTER_PROMPTS = ['Summarize a recent onboarding session', 'Search for adverse media on an entity', 'What high-risk entities should I review?'];

// Dummy session used to render persisted history while no live modal session
// exists yet. Modal-backed fetches (file previews, terminate) won't resolve
// against this — but text/tool_use/tool_result rendering doesn't depend on it.
export const PASSIVE_SESSION: ChatSessionResponse = {
  agentSandboxLogsUrl: '',
  workspaceSandboxLogsUrl: '',
  createdAt: '',
  modalBaseUrl: '',
  sessionId: '',
  sessionToken: '',
  wsUrl: ''
};
const ChatSession = t0 => {
  const $ = _c(133);
  const {
    chatSessionId,
    agentSession,
    onStartNewSession,
    onResumeSession,
    emptyStateLabel,
    forceScrollToBottom,
    onResourceEvent,
    onReady,
    onTurnComplete,
    onApprovalRequired,
    initialMessages,
    isLive,
    isAutopilotEndedConversation,
    starterPrompts,
    replyPlaceholder
  } = t0;
  const {
    session,
    handleConnectionLost,
    isLoading,
    error: sessionError
  } = agentSession;
  let t1;
  if ($[0] !== chatSessionId || $[1] !== handleConnectionLost || $[2] !== initialMessages || $[3] !== isLive || $[4] !== onApprovalRequired || $[5] !== onReady || $[6] !== onResourceEvent || $[7] !== onTurnComplete || $[8] !== session) {
    t1 = {
      session,
      chatSessionId,
      isLive,
      initialMessages,
      onResourceEvent,
      onReady,
      onTurnComplete,
      onApprovalRequired,
      onConnectionLost: handleConnectionLost
    };
    $[0] = chatSessionId;
    $[1] = handleConnectionLost;
    $[2] = initialMessages;
    $[3] = isLive;
    $[4] = onApprovalRequired;
    $[5] = onReady;
    $[6] = onResourceEvent;
    $[7] = onTurnComplete;
    $[8] = session;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const {
    messages,
    pendingMessages,
    sendMessage,
    sendInterrupt,
    sendApproval,
    sendCancelMessage,
    isConnected,
    isThinking,
    error: wsError
  } = useChatWs(t1);
  const rootRef = useRef(null);
  const paneSlotRef = useRef(null);
  const cardSlotRef = useRef(null);
  const reducedMotion = useReducedMotion();
  let t2;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      rootRef,
      paneSlotRef,
      cardSlotRef
    };
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const browserPane = useBrowserPane(t2, messages, session ?? PASSIVE_SESSION);
  let t3;
  if ($[11] !== messages) {
    t3 = deriveChatResources(messages);
    $[11] = messages;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const resources = t3;
  const setChatResources = useSetChatResources();
  let t4;
  let t5;
  if ($[13] !== resources || $[14] !== setChatResources) {
    t4 = () => {
      setChatResources(resources);
      return () => setChatResources({
        items: []
      });
    };
    t5 = [resources, setChatResources];
    $[13] = resources;
    $[14] = setChatResources;
    $[15] = t4;
    $[16] = t5;
  } else {
    t4 = $[15];
    t5 = $[16];
  }
  useEffect(t4, t5);
  let t6;
  if ($[17] !== messages) {
    t6 = deriveBackgroundAgents(messages);
    $[17] = messages;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const backgroundAgents = t6;
  const setBackgroundAgents = useSetBackgroundAgents();
  let t7;
  let t8;
  if ($[19] !== backgroundAgents || $[20] !== setBackgroundAgents) {
    t7 = () => {
      setBackgroundAgents(backgroundAgents);
      return () => setBackgroundAgents({
        items: []
      });
    };
    t8 = [backgroundAgents, setBackgroundAgents];
    $[19] = backgroundAgents;
    $[20] = setBackgroundAgents;
    $[21] = t7;
    $[22] = t8;
  } else {
    t7 = $[21];
    t8 = $[22];
  }
  useEffect(t7, t8);
  const recoveryRef = useRef(false);
  let t10;
  let t9;
  if ($[23] !== onResumeSession || $[24] !== pendingMessages || $[25] !== session) {
    t9 = () => {
      if (session) {
        recoveryRef.current = false;
        return;
      }
      if (recoveryRef.current) {
        return;
      }
      if (pendingMessages.length === 0) {
        return;
      }
      if (!onResumeSession) {
        return;
      }
      recoveryRef.current = true;
      onResumeSession();
    };
    t10 = [session, pendingMessages, onResumeSession];
    $[23] = onResumeSession;
    $[24] = pendingMessages;
    $[25] = session;
    $[26] = t10;
    $[27] = t9;
  } else {
    t10 = $[26];
    t9 = $[27];
  }
  useEffect(t9, t10);
  let t11;
  if ($[28] !== onResumeSession || $[29] !== onStartNewSession || $[30] !== sendMessage || $[31] !== session) {
    t11 = async (content, args) => {
      if (session) {
        return sendMessage(content, args);
      }
      if (onResumeSession) {
        await onResumeSession(content);
        await sendMessage(content, args);
        return;
      }
      await onStartNewSession?.(content, args.files, args.context, args.mentions);
    };
    $[28] = onResumeSession;
    $[29] = onStartNewSession;
    $[30] = sendMessage;
    $[31] = session;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  const handleSendMessage = t11;
  if (isPermissionError(sessionError)) {
    let t12;
    if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <PermissionErrorState />;
      $[33] = t12;
    } else {
      t12 = $[33];
    }
    return t12;
  }
  if ((wsError || sessionError) && !session && messages.length === 0 && pendingMessages.length === 0) {
    let t12;
    if ($[34] !== isLoading || $[35] !== onResumeSession) {
      t12 = <ConnectionErrorState onRetry={onResumeSession} isRetrying={isLoading} />;
      $[34] = isLoading;
      $[35] = onResumeSession;
      $[36] = t12;
    } else {
      t12 = $[36];
    }
    return t12;
  }
  let effectiveSession;
  let t12;
  let t13;
  if ($[37] !== agentSession || $[38] !== chatSessionId || $[39] !== emptyStateLabel || $[40] !== isAutopilotEndedConversation || $[41] !== isLive || $[42] !== messages.length || $[43] !== onStartNewSession || $[44] !== pendingMessages || $[45] !== replyPlaceholder || $[46] !== session || $[47] !== starterPrompts) {
    t13 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const stashedPrompt = peekInitialPrompt(chatSessionId);
      if (messages.length === 0 && pendingMessages.length === 0 && !session && !stashedPrompt) {
        const t14 = starterPrompts ?? DEFAULT_STARTER_PROMPTS;
        let t15;
        if ($[51] !== agentSession || $[52] !== emptyStateLabel || $[53] !== isAutopilotEndedConversation || $[54] !== isLive || $[55] !== onStartNewSession || $[56] !== replyPlaceholder || $[57] !== t14) {
          t15 = <EmptyState label={emptyStateLabel} agentSession={agentSession} startNewSession={onStartNewSession} isLive={isLive} isAutopilotEndedConversation={isAutopilotEndedConversation} starterPrompts={t14} replyPlaceholder={replyPlaceholder} />;
          $[51] = agentSession;
          $[52] = emptyStateLabel;
          $[53] = isAutopilotEndedConversation;
          $[54] = isLive;
          $[55] = onStartNewSession;
          $[56] = replyPlaceholder;
          $[57] = t14;
          $[58] = t15;
        } else {
          t15 = $[58];
        }
        t13 = t15;
        break bb0;
      }
      effectiveSession = session ?? PASSIVE_SESSION;
      t12 = pendingMessages.length === 0 && messages.length === 0 && stashedPrompt ? [{
        type: "pending" as const,
        content: stashedPrompt.prompt,
        file_names: stashedPrompt.files?.map(_temp),
        context: stashedPrompt.context,
        mentions: stashedPrompt.mentions,
        id: ""
      }] : pendingMessages;
    }
    $[37] = agentSession;
    $[38] = chatSessionId;
    $[39] = emptyStateLabel;
    $[40] = isAutopilotEndedConversation;
    $[41] = isLive;
    $[42] = messages.length;
    $[43] = onStartNewSession;
    $[44] = pendingMessages;
    $[45] = replyPlaceholder;
    $[46] = session;
    $[47] = starterPrompts;
    $[48] = effectiveSession;
    $[49] = t12;
    $[50] = t13;
  } else {
    effectiveSession = $[48];
    t12 = $[49];
    t13 = $[50];
  }
  if (t13 !== Symbol.for("react.early_return_sentinel")) {
    return t13;
  }
  const displayPending = t12;
  const browser = browserPane.session;
  const ask = pendingMessages.length > 0 ? null : browserPane.ask;
  const handoff = ask !== null && ask.kind === "handoff";
  let t14;
  if ($[59] !== handleSendMessage) {
    t14 = content_0 => {
      handleSendMessage(content_0, {});
    };
    $[59] = handleSendMessage;
    $[60] = t14;
  } else {
    t14 = $[60];
  }
  const sendText = t14;
  let pinnedBrowser;
  if (browser !== null && ask !== null) {
    let t15;
    if ($[61] !== ask || $[62] !== browser || $[63] !== browserPane.expand || $[64] !== browserPane.paneOpen || $[65] !== browserPane.submitCode || $[66] !== sendText) {
      t15 = <BrowserAskCard key={ask.id} ask={ask} session={browser} paneOpen={browserPane.paneOpen} onOpenBrowser={browserPane.expand} onSendMessage={sendText} onSubmitCode={browserPane.submitCode} />;
      $[61] = ask;
      $[62] = browser;
      $[63] = browserPane.expand;
      $[64] = browserPane.paneOpen;
      $[65] = browserPane.submitCode;
      $[66] = sendText;
      $[67] = t15;
    } else {
      t15 = $[67];
    }
    pinnedBrowser = t15;
  }
  let t15;
  if ($[68] !== browserPane.coversChat || $[69] !== pinnedBrowser) {
    t15 = placeBrowserAsk(pinnedBrowser, browserPane.coversChat);
    $[68] = browserPane.coversChat;
    $[69] = pinnedBrowser;
    $[70] = t15;
  } else {
    t15 = $[70];
  }
  const browserAskSlot = t15;
  const t16 = session ? isConnected : !isLoading;
  const t17 = !!session && !isConnected;
  let t18;
  if ($[71] !== browserAskSlot.chat || $[72] !== displayPending || $[73] !== effectiveSession || $[74] !== forceScrollToBottom || $[75] !== handleSendMessage || $[76] !== isAutopilotEndedConversation || $[77] !== isLive || $[78] !== isThinking || $[79] !== messages || $[80] !== replyPlaceholder || $[81] !== sendApproval || $[82] !== sendCancelMessage || $[83] !== sendInterrupt || $[84] !== t16 || $[85] !== t17) {
    t18 = <ChatPanel messages={messages} pendingMessages={displayPending} session={effectiveSession} isConnected={t16} isThinking={isThinking} isConnecting={t17} onSendMessage={handleSendMessage} onInterrupt={sendInterrupt} onApproval={sendApproval} onCancelPending={sendCancelMessage} forceScrollToBottom={forceScrollToBottom} isLive={isLive} isAutopilotEndedConversation={isAutopilotEndedConversation} placeholderOverride={replyPlaceholder} pinnedBrowser={browserAskSlot.chat} />;
    $[71] = browserAskSlot.chat;
    $[72] = displayPending;
    $[73] = effectiveSession;
    $[74] = forceScrollToBottom;
    $[75] = handleSendMessage;
    $[76] = isAutopilotEndedConversation;
    $[77] = isLive;
    $[78] = isThinking;
    $[79] = messages;
    $[80] = replyPlaceholder;
    $[81] = sendApproval;
    $[82] = sendCancelMessage;
    $[83] = sendInterrupt;
    $[84] = t16;
    $[85] = t17;
    $[86] = t18;
  } else {
    t18 = $[86];
  }
  let t19;
  if ($[87] !== browser || $[88] !== browserPane.dismiss || $[89] !== browserPane.expand || $[90] !== browserPane.previewOpen || $[91] !== isThinking || $[92] !== reducedMotion) {
    t19 = browser !== null && browserPane.previewOpen && <motion.div key="browser-preview" className="absolute top-3 right-3 z-20" initial={{
      opacity: 0,
      transform: "scale(0.96)"
    }} animate={{
      opacity: 1,
      transform: "scale(1)"
    }} transition={{
      duration: reducedMotion ? 0 : CARD_SECONDS,
      ease: EASE_OUT
    }}><BrowserPreviewCard session={browser} working={isThinking} onExpand={browserPane.expand} onDismiss={browserPane.dismiss} frameSlotRef={cardSlotRef} /></motion.div>;
    $[87] = browser;
    $[88] = browserPane.dismiss;
    $[89] = browserPane.expand;
    $[90] = browserPane.previewOpen;
    $[91] = isThinking;
    $[92] = reducedMotion;
    $[93] = t19;
  } else {
    t19 = $[93];
  }
  let t20;
  if ($[94] !== t19) {
    t20 = <AnimatePresence initial={false} mode="wait">{t19}</AnimatePresence>;
    $[94] = t19;
    $[95] = t20;
  } else {
    t20 = $[95];
  }
  let t21;
  if ($[96] !== browserPane.wide || $[97] !== t18 || $[98] !== t20) {
    t21 = <div className="relative flex-1 min-w-0 overflow-x-clip flex flex-col" inert={browserPane.wide}>{t18}{t20}</div>;
    $[96] = browserPane.wide;
    $[97] = t18;
    $[98] = t20;
    $[99] = t21;
  } else {
    t21 = $[99];
  }
  const t22 = browser !== null && browserPane.paneOpen;
  let t23;
  if ($[100] !== browser || $[101] !== browserAskSlot.pane || $[102] !== browserPane.minimize || $[103] !== browserPane.toggleWide || $[104] !== browserPane.wide || $[105] !== handoff || $[106] !== isThinking) {
    t23 = browser !== null && <BrowserPane session={browser} handoff={handoff} working={isThinking} wide={browserPane.wide} onMinimize={browserPane.minimize} onToggleWide={browserPane.toggleWide} footer={browserAskSlot.pane} frameSlotRef={paneSlotRef} />;
    $[100] = browser;
    $[101] = browserAskSlot.pane;
    $[102] = browserPane.minimize;
    $[103] = browserPane.toggleWide;
    $[104] = browserPane.wide;
    $[105] = handoff;
    $[106] = isThinking;
    $[107] = t23;
  } else {
    t23 = $[107];
  }
  let t24;
  if ($[108] !== browserPane.wide || $[109] !== t22 || $[110] !== t23) {
    t24 = <BrowserPaneSlot open={t22} wide={browserPane.wide}>{t23}</BrowserPaneSlot>;
    $[108] = browserPane.wide;
    $[109] = t22;
    $[110] = t23;
    $[111] = t24;
  } else {
    t24 = $[111];
  }
  let t25;
  if ($[112] !== browser || $[113] !== browserPane.frameRect || $[114] !== browserPane.liveViewFailed || $[115] !== browserPane.liveViewUrl || $[116] !== browserPane.paneOpen || $[117] !== browserPane.remoteViewport || $[118] !== browserPane.resizing || $[119] !== handoff || $[120] !== isThinking) {
    t25 = browser !== null && browserPane.frameRect !== null && <BrowserLiveView liveViewUrl={browserPane.liveViewUrl} liveViewFailed={browserPane.liveViewFailed} remoteViewport={browserPane.remoteViewport} resizing={browserPane.resizing} rect={browserPane.frameRect} fit={browserPane.paneOpen ? "contain" : "cover"} interactive={browserPane.paneOpen && (handoff || !isThinking)} />;
    $[112] = browser;
    $[113] = browserPane.frameRect;
    $[114] = browserPane.liveViewFailed;
    $[115] = browserPane.liveViewUrl;
    $[116] = browserPane.paneOpen;
    $[117] = browserPane.remoteViewport;
    $[118] = browserPane.resizing;
    $[119] = handoff;
    $[120] = isThinking;
    $[121] = t25;
  } else {
    t25 = $[121];
  }
  let t26;
  if ($[122] !== t21 || $[123] !== t24 || $[124] !== t25) {
    t26 = <div ref={rootRef} className="relative flex flex-1 h-full min-h-0 min-w-0">{t21}{t24}{t25}</div>;
    $[122] = t21;
    $[123] = t24;
    $[124] = t25;
    $[125] = t26;
  } else {
    t26 = $[125];
  }
  let t27;
  if ($[126] !== messages || $[127] !== onStartNewSession || $[128] !== session) {
    t27 = <PercyDevtools messages={messages} onStartSession={onStartNewSession} session={session} />;
    $[126] = messages;
    $[127] = onStartNewSession;
    $[128] = session;
    $[129] = t27;
  } else {
    t27 = $[129];
  }
  let t28;
  if ($[130] !== t26 || $[131] !== t27) {
    t28 = <>{t26}{t27}</>;
    $[130] = t26;
    $[131] = t27;
    $[132] = t28;
  } else {
    t28 = $[132];
  }
  return t28;
};

// A pane covering the chat hides the pinned stack too, so the ask moves into the pane.
const placeBrowserAsk = (ask: ReactNode, coversChat: boolean): {
  chat?: ReactNode;
  pane?: ReactNode;
} => coversChat ? {
  pane: ask
} : {
  chat: ask
};
const ConnectionErrorState = t0 => {
  const $ = _c(8);
  const {
    onRetry,
    isRetrying
  } = t0;
  let t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <IcoWarning40 color="error" />;
    t2 = <p className="text-heading-4 font-bold text-primary">Couldn't load this chat</p>;
    t3 = <p className="text-body-2 text-tertiary">Check your connection and try again.</p>;
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
  }
  let t4;
  if ($[3] !== isRetrying || $[4] !== onRetry) {
    t4 = onRetry && <Button variant="secondary" onClick={onRetry} loading={isRetrying}>Try again</Button>;
    $[3] = isRetrying;
    $[4] = onRetry;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t4) {
    t5 = <div className="relative flex flex-col h-full"><div className="flex-1 flex flex-col items-center justify-center px-4 pb-4 pt-4"><div className="flex flex-col items-center gap-4 text-center max-w-[300px]">{t1}{t2}{t3}{t4}</div></div></div>;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
};
const PermissionErrorState = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="relative flex flex-col h-full"><div className="flex-1 flex flex-col items-center justify-center px-4 pb-4 pt-4"><div className="flex flex-col items-center gap-4 text-center max-w-[300px]"><IcoForbid40 color="tertiary" /><p className="text-heading-4 font-bold text-primary">Permission required</p><p className="text-body-2 text-tertiary">You don't have permission to start a chat.</p></div></div></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
type EmptyStateProps = {
  label: string;
  agentSession: AgentSessionInfo;
  startNewSession?: StartNewChat;
  isLive?: boolean;
  isAutopilotEndedConversation?: boolean;
  starterPrompts?: string[];
  replyPlaceholder?: string;
};
const EmptyState = t0 => {
  const $ = _c(35);
  const {
    label,
    agentSession,
    startNewSession,
    isLive,
    isAutopilotEndedConversation,
    starterPrompts,
    replyPlaceholder
  } = t0;
  const {
    isLoading
  } = agentSession;
  const {
    t
  } = useTranslation("copilot");
  const {
    effectiveContext
  } = useResourceContextSelection();
  const inputRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      mentionEntity: attrs => inputRef.current?.mentionEntity(attrs)
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const mentionContext = t1;
  let t2;
  if ($[1] !== isLive || $[2] !== t) {
    t2 = composerActions({
      env: isLive === undefined ? undefined : {
        value: isLive ? "production" : "sandbox",
        lockedTooltip: t(_temp2)
      }
    });
    $[1] = isLive;
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const actions = t2;
  let t3;
  if ($[4] !== isLoading || $[5] !== label || $[6] !== t) {
    t3 = <div className="flex-1 flex flex-col items-center overflow-y-auto px-4 pb-4 pt-4"><div className="max-w-[720px] w-full mx-auto flex flex-col items-center flex-1">{isLoading ? <div className="flex flex-col items-center justify-center gap-4 flex-1"><LoadingSpinner size="large" color="primary" /><p className="text-body-2 text-shimmer">{t(_temp3)}</p></div> : <div className="flex flex-col items-center gap-3 text-center px-4 mt-auto"><div className="flex items-center justify-center w-9 h-9 rounded-full bg-neutral-inverted"><IcoSparkles16 color="quinary" /></div><p className="text-body-3 text-tertiary">{label}</p></div>}</div></div>;
    $[4] = isLoading;
    $[5] = label;
    $[6] = t;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="absolute -top-6 left-0 right-0 h-6 bg-gradient-to-t from-primary to-transparent pointer-events-none" />;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== effectiveContext || $[10] !== isLoading || $[11] !== startNewSession || $[12] !== starterPrompts) {
    t5 = !isLoading && starterPrompts && starterPrompts.length > 0 && <FollowUpSuggestions suggestions={starterPrompts} layout="wrap" onSelect={prompt => startNewSession?.(prompt, [], effectiveContext)} />;
    $[9] = effectiveContext;
    $[10] = isLoading;
    $[11] = startNewSession;
    $[12] = starterPrompts;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== isAutopilotEndedConversation || $[15] !== replyPlaceholder || $[16] !== t) {
    t6 = isAutopilotEndedConversation ? t(_temp4) : replyPlaceholder ?? t(_temp5);
    $[14] = isAutopilotEndedConversation;
    $[15] = replyPlaceholder;
    $[16] = t;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== startNewSession) {
    t7 = async (content, t8) => {
      const {
        files,
        context,
        mentions
      } = t8;
      await startNewSession?.(content, files, context, mentions);
    };
    $[18] = startNewSession;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== actions || $[21] !== isLive || $[22] !== isLoading || $[23] !== t6 || $[24] !== t7) {
    t8 = <ChatInput ref={inputRef} placeholder={t6} disabled={isLoading} isLive={isLive} onSendMessage={t7} {...actions} />;
    $[20] = actions;
    $[21] = isLive;
    $[22] = isLoading;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== t5 || $[27] !== t8) {
    t9 = <div className="flex w-full flex-col items-center gap-3">{t5}{t8}</div>;
    $[26] = t5;
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <ComposerDisclaimer className="mt-3 w-full" />;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== t9) {
    t11 = <div className="relative px-3 pb-[max(1rem,env(safe-area-inset-bottom))] group-data-[keyboard-open]/app:pb-3 flex flex-col items-center max-w-[720px] w-full mx-auto">{t4}{t9}{t10}</div>;
    $[30] = t9;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== t11 || $[33] !== t3) {
    t12 = <ChatInputMentionContext.Provider value={mentionContext}><div className="relative flex flex-col h-full">{t3}{t11}</div></ChatInputMentionContext.Provider>;
    $[32] = t11;
    $[33] = t3;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  return t12;
};
export default ChatSession;
function _temp(f) {
  return f.name;
}
function _temp2($) {
  return $.chat.envLocked;
}
function _temp3($_0) {
  return $_0.chat.loadingText;
}
function _temp4($_1) {
  return $_1.chat.startNewConversationPlaceholder;
}
function _temp5($_2) {
  return $_2.chat.replyPlaceholder;
}